import store from '@/store/index'
import { getInstance } from '@/auth/authWrapper'
import cfg from '@root/config'
import { getFirstProjectByName } from '@/utils'
import {
    shouldSuppressPublicMarketplaceRedirect,
    toProjectScopedRedirectPath,
} from '@/helpers/redirectAfterOnboarding'

export function isPublicMarketplacePath(path: string): boolean {
    return path === '/marketplace' || path.startsWith('/marketplace/')
}

export function getMarketplaceProjectKeyFromRoute(params: { id?: string }): string {
    return params?.id ?? 'unauthorized'
}

export const waitForProjectsLoaded = () =>
    new Promise<void>((resolve) => {
        if (!store.state.isProjectsLoading) {
            resolve()
            return
        }
        const unwatch = store.watch(
            (state) => state.isProjectsLoading,
            (projectsLoading) => {
                if (!projectsLoading) {
                    unwatch()
                    resolve()
                }
            }
        )
    })

const waitForAuthReady = (authInstance: ReturnType<typeof getInstance>) =>
    new Promise<void>((resolve) => {
        if (!authInstance.loading) {
            resolve()
            return
        }
        const unwatch = authInstance.$watch('loading', (loading: boolean) => {
            if (!loading) {
                unwatch()
                resolve()
            }
        })
    })

export function getFirstProjectIdAlphabetically(): string | null {
    const projects = store.state.projects || []
    return getFirstProjectByName(projects)?.id ?? null
}

export function resolveProjectMarketplaceRedirectPath(
    to: { name?: string | null; params?: Record<string, string> },
    projectId: string,
): string | null {
    if (to.name === 'marketplaceUnauthorized') {
        return `/project/${projectId}/marketplace`
    }
    if (to.name === 'marketplaceIntegrationUnauthorized') {
        const integrationId = to.params?.integrationId
        if (!integrationId) return null
        return `/project/${projectId}/marketplace/integration/${integrationId}`
    }
    return null
}

async function ensureAllProjectsLoaded() {
    if (store.state.project?.areAllProjectsLoaded) {
        return
    }
    await store.dispatch('project/setProjects')
}

/** Redirects authenticated users with projects from /marketplace/* to project-scoped marketplace. */
export async function marketplaceUnauthorizedRouteGuard(
    to: { name?: string | null; params?: Record<string, string>; query?: Record<string, any> },
    _from: unknown,
    next: (location?: string | { path: string; replace?: boolean; query?: Record<string, any> }) => void
) {
    const authInstance = getInstance()
    await waitForAuthReady(authInstance)

    if (!authInstance.isAuthenticated) {
        next()
        return
    }

    if (shouldSuppressPublicMarketplaceRedirect()) {
        next()
        return
    }

    await waitForProjectsLoaded()
    await ensureAllProjectsLoaded()

    const projectId = getFirstProjectIdAlphabetically()
    if (!projectId) {
        next()
        return
    }

    const redirectPath = resolveProjectMarketplaceRedirectPath(to, projectId)
    if (redirectPath) {
        next({ path: redirectPath, replace: true, query: to.query })
        return
    }

    next()
}

export function hasAtLeastOneProject(): boolean {
    return (store.state.projects || []).length > 0
}

type MarketplaceQuery = Record<string, string | (string | null)[] | null | undefined>

type RouterPushLike = {
    push: (
        location: { path: string; replace?: boolean; query?: MarketplaceQuery } | string
    ) => Promise<unknown> | unknown
}

/** После входа: редирект с публичного маркетплейса на проект (первый по алфавиту). */
export async function redirectAuthenticatedUserToProjectMarketplace(
    publicPath: string,
    router: RouterPushLike,
    routeMeta?: { name?: string | null; params?: Record<string, string>; query?: MarketplaceQuery },
): Promise<boolean> {
    const authInstance = getInstance()
    await waitForAuthReady(authInstance)
    if (!authInstance.isAuthenticated) return false

    await waitForProjectsLoaded()
    await ensureAllProjectsLoaded()

    const projectId = getFirstProjectIdAlphabetically()
    if (!projectId) return false

    const redirectPath =
        (routeMeta && resolveProjectMarketplaceRedirectPath(routeMeta, projectId)) ||
        toProjectScopedRedirectPath(publicPath, projectId)

    try {
        await router.push({ path: redirectPath, replace: true, query: routeMeta?.query })
    } catch {
        // ignore: duplicate navigation
    }
    return true
}

/** Redirects users without auth or projects from /project/:id/marketplace/* to public marketplace. */
export async function marketplaceProjectRouteGuard(
    to: { name?: string | null; params?: Record<string, string> },
    _from: unknown,
    next: (location?: string | { path: string; replace?: boolean }) => void
) {
    const authInstance = getInstance()
    await waitForAuthReady(authInstance)

    // Юзер уже в контексте проекта (/project/:id/marketplace) — полный список проектов
    // для этого не нужен. Авторизован → показываем; гость → публичный маркетплейс.
    if (authInstance.isAuthenticated) {
        next()
        return
    }

    if (to.name === 'marketplaceIntegration') {
        const integrationId = to.params?.integrationId
        next({
            path: `/marketplace/integration/${integrationId}`,
            replace: true,
        })
        return
    }

    next({ path: '/marketplace', replace: true })
}
export async function billingManagerRouteGuard(to: any, _from: any, next: (value?: any) => void) {
    const auth = getInstance()
    await waitForAuthReady(auth)
    if (auth?.isAuthenticated && cfg.adminsWhitelist.includes(auth.user?.sub)) {
        next()
        return
    }
    next(`/project/${to.params.id}/billing/`)
}
